import { CommonModule } from "@angular/common";
import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormControl, ReactiveFormsModule, Validators } from "@angular/forms";
import { TranslocoService } from "@jsverse/transloco";
import { FieldType, FieldTypeConfig } from "@ngx-formly/core";
import { DatePickerModule } from "primeng/datepicker";
import { Subscription } from "rxjs";

/**
  Example usage:
 
  {
    key: "startDate",
    type: "calendar",
    props: {
      label: {
        en: "Calendar label",
        fi: "Kalenterin otsikko",
        sv: "Kalender rubrik",
      },                                  // Optional: Label displayed above the input
      required: true,                     // Optional: Makes the field required
      placeholder: {
        en: "Select a date",
        fi: "Valitse päivämäärä",
        sv: "Välj ett datum",
      },                                  // Optional: Placeholder text in the input
      description: {
        en: "Select a date",
        fi: "Valitse päivämäärä",
        sv: "Välj ett datum",
      },                                  // Optional: Description text below the input
      minDate: new Date(),                // Optional: Minimum selectable date
      maxDate: new Date("2025-12-31"),    // Optional: Maximum selectable date
    }
  },


  ////////////

  JSON example:
  {
    "key": "startDate",
    "type": "calendar",
    "props": {
      "label": {
        "en": "Calendar label",
        "fi": "Kalenterin otsikko",
        "sv": "Kalender rubrik"
      },
      "required": true,
      "placeholder": {
        "en": "Select a date",
        "fi": "Valitse päivämäärä",
        "sv": "Välj ett datum"
      },
      "description": {
        "en": "Select a date",
        "fi": "Valitse päivämäärä",
        "sv": "Välj ett datum"
      },
      "minDate": "2025-07-15",                // NOT YET POSSIBLE (should add support for string parsing to date)
      "maxDate": "2025-12-31"    // NOT YET POSSIBLE (should add support for string parsing to date)
    }
  },
*/

@Component({
  selector: "app-calendar",
  templateUrl: "./calendar.type.html",
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, DatePickerModule],
})
export class CalendarComponent extends FieldType<FieldTypeConfig> implements OnInit, OnDestroy {
  /* TODO: Add support for string parsing to date (minDate, maxDate) */
  /* TODO: Add support for presetting values from the model with strings like "today", "today + 2 weeks", "today + -5 days", etc. */
  dateFormControl = new FormControl<Date | null>(null);
  firstDayOfWeek = 1;

  private valueSub?: Subscription;

  constructor(private translocoService: TranslocoService) {
    super();
  }

  ngOnInit(): void {
    const required = !!this.props.required;
    if (required) {
      this.dateFormControl.setValidators(Validators.required);
    }

    const initialValue = this.formControl.value;
    if (initialValue) {
      this.dateFormControl.setValue(new Date(initialValue));
    }

    this.valueSub = this.dateFormControl.valueChanges.subscribe((value) => {
      this.formControl.setValue(value, { emitEvent: false });
      this.formControl.markAsDirty();
      this.syncValidity();
    });

    // Subscribe to parent value changes to avoid circular updates
    const parentSub = this.formControl.valueChanges.subscribe((value) => {
      // Only update if different to avoid circular updates
      if (value !== this.dateFormControl.value) {
        this.dateFormControl.setValue(value ? new Date(value) : null, { emitEvent: false });
      }
    });

    this.valueSub.add(parentSub);

    this.syncValidity(); // Initial check
  }

  ngOnDestroy(): void {
    this.valueSub?.unsubscribe();
  }

  onBlur(): void {
    this.dateFormControl.markAsTouched();
    this.formControl.markAsTouched();
    this.syncValidity();
  }

  isFieldInvalid(): boolean {
    return this.dateFormControl.invalid && (this.dateFormControl.dirty || this.dateFormControl.touched);
  }

  getErrorMessage(): string {
    if (this.dateFormControl.hasError("required")) {
      return this.translocoService.translate("app.formly-types.validationMessages.fieldRequired");
    }
    return this.translocoService.translate("app.formly-types.validationMessages.invalidDate");
  }

  private syncValidity(): void {
    if (this.dateFormControl.invalid) {
      this.formControl.setErrors({ invalid: true });
    } else {
      const { ...errors } = this.formControl.errors ?? {};
      this.formControl.setErrors(Object.keys(errors).length ? errors : null);
    }
  }
}
